/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Output,
	ViewEncapsulation,
	forwardRef,
	inject,
	input,
	viewChild,
} from "@angular/core";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { PopoverDirective } from "../floating/popover/popover.directive";
import { PopoverModule } from "../floating/popover/popover.module";
import { TooltipModule } from "../floating/tooltip/tooltip.module";
import { IconComponent } from "../icon/icon.component";

export interface CbTime {
	hour: number | null;
	minute: number | null;
	second?: number | null;
}

const noop = () => {};

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => TimepickerComponent),
	multi: true,
};

@Component({
	selector: "cb-timepicker",
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		FormsModule,
		IconComponent,
		TooltipModule,
		PopoverModule,
		TooltipModule,
	],
	templateUrl: "./timepicker.component.html",
	styleUrl: "./timepicker.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	animations: [
		trigger("ContentAnimation", [
			transition(":enter", [
				style({ opacity: 0, transform: "scale(1.1)" }),
				animate(
					"100ms ease-out",
					style({ opacity: "1", transform: "scale(1)" }),
				),
			]),
			transition(":leave", [
				style({ opacity: 1, transform: "scale(1)" }),
				animate(
					"100ms ease-out",
					style({ opacity: "0", transform: "scale(0.9)" }),
				),
			]),
		]),
	],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimepickerComponent implements ControlValueAccessor {
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

	public readonly placeholder = input<string>();

	/**
	 * Label of the input
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the input
	 */
	public readonly hint = input<string>();

	/**
	 * Info of the input displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the input is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the input is disabled
	 */
	public readonly disabled = input(false);

	/**
	 * Whether to show the reset button
	 */
	public readonly resetButton = input<boolean | undefined>(true);

	/**
	 * Leading icon of the input
	 */
	public readonly icon = input("cb-base-outline-clock");

	/**
	 * Whether the input has an error
	 */
	public readonly error = input<boolean>();

	public readonly minTime = input(0);
	public readonly maxTime = input<number>(23 * 60 * 60);

	public readonly hasSeconds = input(false);
	public readonly step = input(1);
	public readonly initialTime = input<number>();

	@Output() public change: EventEmitter<number> = new EventEmitter<number>();

	readonly popover = viewChild(PopoverDirective);

	private innerValue?: CbTime;

	public get value(): CbTime | undefined {
		return this.innerValue;
	}

	public set value(v: CbTime | undefined) {
		if (v !== this.innerValue) {
			this.innerValue = v;

			this.onChangeCallback(this.totalSeconds);
			this.change.emit(this.totalSeconds);
		}
	}

	public get canReset(): boolean {
		return this.resetButton() === true && this.value !== undefined;
	}

	public get strValue(): string | undefined {
		if (this.value?.hour === null || this.value?.minute === null) {
			return undefined;
		}
		const hour = this.value?.hour.toString().padStart(2, "0");

		const minute = this.value?.minute.toString().padStart(2, "0");

		const second = this.value?.second
			? this.value.second.toString().padStart(2, "0")
			: "00";

		return `${hour}:${minute}${this.hasSeconds() ? `:${second}` : ""}`;
	}

	private get totalSeconds(): number {
		return (this.value?.hour || 0) * 3600 + (this.value?.minute || 0) * 60;
	}

	public writeValue(obj: number | undefined): void {
		if (obj !== this.innerValue) {
			if (obj) {
				const hour = Math.floor(obj / 3600) % 24;
				const minute = Math.floor((obj % 3600) / 60);
				const second = this.hasSeconds() ? obj % 60 : undefined;

				this.innerValue = {
					hour,
					minute,
					second,
				};
			} else {
				this.value = undefined;
			}
		}
	}

	public reset(): void {
		this.value = undefined;
		this.popover()?.close();
		this.cdr.detectChanges();
	}

	public setStartValue() {
		const initialValue = this.initialTime();

		let hour: number | null = null;
		let minute: number | null = null;
		let second: number | null = null;

		if (initialValue) {
			hour = Math.floor(initialValue / 3600) % 24;
			minute = Math.floor((initialValue % 3600) / 60);
			second = this.hasSeconds() ? initialValue % 60 : null;
		}

		if (!this.value) {
			if (this.hasSeconds()) {
				this.value = {
					hour,
					minute,
					second,
				};
			} else {
				this.value = {
					hour,
					minute,
				};
			}
		}
	}

	validateTime(unit: "hour" | "minute" | "second", v: number | null): void {
		const value: number = v || 0;
		if (this.value) {
			switch (unit) {
				case "hour":
					if (value < 0) {
						this.value.hour = 0;
					} else if (value > 23) {
						this.value.hour = value % 24;
					}
					break;
				case "minute":
					if (value < 0) {
						this.value.minute = 0;
					} else if (value > 59) {
						this.value.minute = value % 60;
					}
					break;
				case "second":
					if (this.hasSeconds() && this.value.second !== undefined) {
						if (value < 0) {
							this.value.second = 0;
						} else if (value > 59) {
							this.value.second = value % 60;
						}
					}
					break;
			}
		}
	}

	increment(unit: "hour" | "minute" | "second"): void {
		if (this.value) {
			switch (unit) {
				case "hour":
					if (this.value.hour === null) this.value.hour = 0;

					if (this.value.hour !== null) {
						this.value.hour = (this.value.hour + this.step()) % 24;
					}
					break;
				case "minute":
					if (this.value.minute === null) this.value.minute = 0;

					if (this.value.minute !== null) {
						this.value.minute = (this.value.minute + this.step()) % 60;
					}
					break;

				case "second":
					if (this.value.second === null) this.value.second = 0;

					if (
						this.hasSeconds() &&
						this.value.second !== undefined &&
						this.value.second !== null
					)
						this.value.second = (this.value.second + this.step()) % 60;
					break;
			}
			this.onChangeCallback(this.totalSeconds);
			this.change.emit(this.totalSeconds);
			this.cdr.detectChanges();
		}
	}

	decrement(unit: "hour" | "minute" | "second"): void {
		if (this.value) {
			switch (unit) {
				case "hour":
					if (this.value.hour !== null && this.value.hour > 0) {
						this.value.hour -= this.step();
					}
					break;
				case "minute":
					if (this.value.minute !== null && this.value.minute > 0) {
						this.value.minute -= this.step();
					}

					break;

				case "second":
					if (
						this.hasSeconds() &&
						this.value.second !== undefined &&
						this.value.second !== null &&
						this.value.second > 0
					)
						this.value.second -= this.step();
					break;
			}
			this.onChangeCallback(this.totalSeconds);

			this.change.emit(this.totalSeconds);
			this.cdr.detectChanges();
		}
	}

	selectAll(event: FocusEvent): void {
		const inputElement = event.target as HTMLInputElement;
		setTimeout(() => {
			inputElement.select();
		}, 0);
	}

	onUserInput(event: Event, unit: "hour" | "minute" | "second"): void {
		const inputElement = event.target as HTMLInputElement;
		const max = this.getMaxValue(unit);

		// remove first 0 from inputElement.value

		let inputValue = inputElement.value.replace(/^0+/, "");

		// let inputValue = inputElement.value;
		let numericValue = Number.parseInt(inputValue, 10);

		// Prevent the user from entering more than 2 digits

		if (inputValue.length === 2) {
			if (numericValue > max) {
				inputValue = inputValue.slice(-1);
			}
		}
		if (inputValue.length > 2) {
			if (numericValue > max) {
				inputValue = inputValue.slice(0, 2);
			} else {
				inputValue = inputValue.slice(1, 3);
			}
		}

		numericValue = Number.parseInt(inputValue, 10);

		// Update the component's value based on the input change
		if (this.value) {
			switch (unit) {
				case "hour":
					this.value.hour = numericValue || 0;
					break;
				case "minute":
					this.value.minute = numericValue || 0;
					break;
				case "second":
					if (this.hasSeconds() && this.value.second !== undefined) {
						this.value.second = numericValue || 0;
					}
					break;
			}
		}

		inputValue = inputValue.padStart(2, "0");

		inputElement.value = inputValue;
		this.onChangeCallback(this.totalSeconds);

		this.change.emit(this.totalSeconds);
		this.cdr.detectChanges();
	}

	getMaxValue(unit: "hour" | "minute" | "second"): number {
		if (unit === "hour") {
			return 23;
		}
		return 59;
	}

	public onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}
}
